// SorterMap.js — OLM1 sorter heatmap (layout of the SCADA "SORTER CP01 CP02" mimic).
// Colors each conveyor / chute bed by how often it raised a SCADA alarm, from the
// JamBot auto events (JAMCTRL_AUTO_<date>/<shift>, written by backend/scada_jam_sync.py):
//   AGL events / AGL minutes = Full Alarm on the bed; Jams = jam on the bed or its photo-eyes.
// Bed names are an append-only log (SORTER_BED_NAMES/log via fclm/scan-append):
// { op: 'name', id, name, by, at } — the latest entry per bed wins; nothing is deleted.
// Chute beds default to DD103-1 ... DD116-4 (sorterLayout CHUTES); a saved name
// overrides, and saving an empty name goes back to the default.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BEDS, PES, DEFAULT_NAMES } from '../sorterLayout';
import OEEPanel from './OEEPanel';

const pad = n => String(n).padStart(2, '0');
const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const BED_BY_UPPER = new Map(BEDS.map(b => [b[0].toUpperCase(), b[0]]));
const bedId = k => BED_BY_UPPER.get(String(k).toUpperCase()) || null;
const PE_IDS = new Set(PES.map(p => p[0]));
const NAMES_KEY = { date: 'SORTER_BED_NAMES', shift: 'log' };
const METRICS = { agl: 'AGL events (Full Alarm)', aglmin: 'AGL minutes', jams: 'Jams' };
const RANGES = { shift: 'Selected shift', day: 'Selected day (both shifts)', week: 'Last 7 days' };

// SCADA source id -> { bed, pe } on the layout (null when the device is not on the mimic).
export function locate(src) {
  const s = String(src || '').split('/').pop();
  if (s.startsWith('PE_')) {
    const pe = PE_IDS.has(s) ? s : null;
    const m = s.match(/^PE_(\d+[A-Z]?)_\d+$/);
    const bed = m ? bedId(`CONV_${m[1]}`) || bedId(m[1]) : null;
    return { bed, pe };
  }
  const t = s.replace(/^MDR_/, '');
  return { bed: bedId(t) || bedId(t.replace(/_\d+$/, '')), pe: null };
}

// White -> gold -> red by share of the max.
export function heat(v, max) {
  if (!v) return '#F2F4F4';
  const f = Math.min(1, v / Math.max(1, max));
  const mix = (a, b, t) => Math.round(a + (b - a) * t);
  const [c0, c1] = f < 0.5 ? [[255, 243, 196], [242, 194, 62]] : [[242, 194, 62], [222, 27, 27]];
  const t = f < 0.5 ? f / 0.5 : (f - 0.5) / 0.5;
  return `rgb(${mix(c0[0], c1[0], t)},${mix(c0[1], c1[1], t)},${mix(c0[2], c1[2], t)})`;
}

function defaultShift() {
  const now = new Date();
  const m = now.getHours() * 60 + now.getMinutes();
  const d = new Date(now);
  if (m < 8 * 60) { d.setDate(d.getDate() - 1); return { date: ymd(d), shift: 'night' }; }
  return { date: ymd(d), shift: m >= 18 * 60 + 45 ? 'night' : 'day' };
}

export default function SorterMap({ api, site, user }) {
  const init = useMemo(defaultShift, []);
  const [date, setDate] = useState(init.date);
  const [shift, setShift] = useState(init.shift);
  const [range, setRange] = useState('shift');
  const [metric, setMetric] = useState('agl');
  const [events, setEvents] = useState([]);
  const [nameLog, setNameLog] = useState([]);
  const [sel, setSel] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [status, setStatus] = useState('');

  const get = useCallback((d, st) => fetch(`${api}/api/fclm/cache?site=${site}&date=${d}&shift_type=${st}`)
    .then(r => (r.ok ? r.json() : null)).catch(() => null), [api, site]);

  const load = useCallback(async () => {
    const keys = [];
    if (range === 'shift') keys.push([date, shift]);
    else {
      const n = range === 'week' ? 7 : 1;
      const base = new Date(`${date}T12:00:00`);
      for (let i = 0; i < n; i++) { const d = new Date(base); d.setDate(d.getDate() - i); keys.push([ymd(d), 'day'], [ymd(d), 'night']); }
    }
    const [names, ...res] = await Promise.all([get(NAMES_KEY.date, NAMES_KEY.shift), ...keys.map(([d, s]) => get(`JAMCTRL_AUTO_${d}`, s))]);
    setNameLog(names && Array.isArray(names.scans) ? names.scans : []);
    const all = res.flatMap(r => (r && Array.isArray(r.events) ? r.events : []));
    setEvents(all);
    setStatus(`${all.length} JamBot events loaded`);
  }, [get, date, shift, range]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 60 * 1000); return () => clearInterval(t); }, [load]);

  const names = useMemo(() => {
    const m = { ...DEFAULT_NAMES };
    for (const e of nameLog) {
      if (!e || e.op !== 'name' || !e.id) continue;
      if (e.name) m[e.id] = e.name; else if (DEFAULT_NAMES[e.id]) m[e.id] = DEFAULT_NAMES[e.id]; else delete m[e.id];
    }
    return m;
  }, [nameLog]);

  // Per-bed and per-photo-eye totals for the chosen metric.
  const agg = useMemo(() => {
    const bed = {}, pe = {}, srcs = {}, unplaced = {};
    for (const e of events) {
      const isAgl = e.type === 'AGL', isJam = e.type === 'Jams';
      if (metric === 'jams' ? !isJam : !isAgl) continue;
      const v = metric === 'aglmin' ? Math.max(1, Math.round(((Number(e.endTs) || 0) - (Number(e.startTs) || 0)) / 60)) : 1;
      const loc = locate(e.src);
      if (loc.bed) { bed[loc.bed] = (bed[loc.bed] || 0) + v; (srcs[loc.bed] = srcs[loc.bed] || new Set()).add(e.src); }
      if (loc.pe) pe[loc.pe] = (pe[loc.pe] || 0) + v;
      if (!loc.bed && !loc.pe) unplaced[e.src] = (unplaced[e.src] || 0) + v;
    }
    const max = Math.max(1, ...Object.values(bed), ...Object.values(pe));
    return { bed, pe, srcs, unplaced, max };
  }, [events, metric]);

  const saveName = async () => {
    if (!sel) return;
    const res = await fetch(`${api}/api/fclm/scan-append`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site, date: NAMES_KEY.date, shift_type: NAMES_KEY.shift, scans: [{ op: 'name', id: sel, name: draftName.trim().slice(0, 40), by: user || 'unknown', at: new Date().toISOString() }] }),
    });
    setStatus(res.ok ? `Saved name for ${sel}` : 'Save failed — try again');
    if (res.ok) load();
  };
  const pick = id => { setSel(id); setDraftName(names[id] || ''); };

  // Geometry: the mimic's own pixel space. public/sorter-mimic.png is the SCADA
  // DetailedView (2200x1400 viewport, greyed) cropped to x 70-2200, y 50-1400; the bed
  // boxes from the JamBot inventory sit on the same pixels.
  const BG = { x: 70, y: 50, w: 2130, h: 1350 };
  const CHUTE_LABEL_Y = 1414;
  const top = [...Object.entries(agg.bed)].sort((a, b) => b[1] - a[1]).slice(0, 15);

  const card = { background: '#fff', border: '1px solid #E7E9E9', borderRadius: 8, padding: 16, marginBottom: 16 };
  const th = { textAlign: 'left', fontSize: 12, color: '#697576', padding: '6px 8px', borderBottom: '1px solid #E7E9E9' };
  const td = { fontSize: 13, padding: '6px 8px', borderBottom: '1px solid #E7E9E9', whiteSpace: 'nowrap' };

  if (site !== 'OLM1') return <div><h2 className="section-title">Sorter Map — {site}</h2><p>The sorter map is OLM1 only.</p></div>;
  return (
    <div>
      <h2 className="section-title">Sorter Map — {site}</h2>
      <div style={{ ...card, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12 }}>Date<br /><input type="date" value={date} onChange={e => setDate(e.target.value)} /></label>
        <label style={{ fontSize: 12 }}>Shift<br /><select value={shift} onChange={e => setShift(e.target.value)} disabled={range !== 'shift'}><option value="day">Day</option><option value="night">Night</option></select></label>
        <label style={{ fontSize: 12 }}>Range<br /><select value={range} onChange={e => setRange(e.target.value)}>{Object.entries(RANGES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
        <label style={{ fontSize: 12 }}>Heat by<br /><select value={metric} onChange={e => setMetric(e.target.value)}>{Object.entries(METRICS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
        <span style={{ fontSize: 12, color: '#697576' }}>{status} · click a bed to name it</span>
      </div>

      <div style={card}>
        <svg viewBox={`${BG.x} ${BG.y} ${BG.w} ${BG.h + 26}`} style={{ width: '100%' }} role="img" aria-label={`OLM1 sorter heatmap, ${METRICS[metric]}`}>
          <image href={`${process.env.PUBLIC_URL || ''}/sorter-mimic.png`} x={BG.x} y={BG.y} width={BG.w} height={BG.h} />
          {BEDS.map(([id, x, y, w, h]) => {
            const v = agg.bed[id] || 0;
            // Default chute names show once per chute along the bottom; a custom name shows over its bed.
            const custom = names[id] && names[id] !== DEFAULT_NAMES[id] ? names[id] : '';
            const chuteHead = DEFAULT_NAMES[id] && DEFAULT_NAMES[id].endsWith('-1') ? DEFAULT_NAMES[id].split('-')[0] : '';
            return (
              <g key={id} onClick={() => pick(id)} style={{ cursor: 'pointer' }}>
                <rect x={x - 4} y={y - 4} width={w + 8} height={h + 8} rx="6"
                  fill={v ? heat(v, agg.max) : 'rgba(255,255,255,0.01)'} fillOpacity={v ? 0.92 : 1}
                  stroke={sel === id ? '#077398' : v ? '#232F3E' : 'rgba(7,115,152,0.35)'} strokeWidth={sel === id ? 5 : v ? 2 : 1.5}>
                  <title>{`${id}${names[id] ? ` — ${names[id]}` : ''}: ${v} ${METRICS[metric]}`}</title>
                </rect>
                {v > 0 && <text x={x + w / 2} y={y + h / 2 + 6} fontSize="17" fontWeight="700" textAnchor="middle" fill={v / agg.max > 0.6 ? '#fff' : '#232F3E'} pointerEvents="none">{v}</text>}
                {custom && <text x={x + w / 2} y={y - 8} fontSize="14" fontWeight="700" textAnchor="middle" fill="#077398" pointerEvents="none">{custom}</text>}
                {chuteHead && <text x={x + w / 2} y={CHUTE_LABEL_Y} fontSize="16" fontWeight="700" textAnchor="middle" fill="#232F3E" pointerEvents="none">{chuteHead}</text>}
              </g>
            );
          })}
          {PES.map(([id, x, y]) => {
            const v = agg.pe[id] || 0;
            return v ? <circle key={id} cx={x} cy={y} r="8" fill={heat(v, agg.max)} stroke="#DE1B1B" strokeWidth="2"><title>{`${id}: ${v} ${METRICS[metric]}`}</title></circle> : null;
          })}
        </svg>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 12, marginTop: 6 }}>
          <span>0</span>
          <span style={{ width: 160, height: 10, background: `linear-gradient(90deg, ${heat(0.01, 1)}, ${heat(0.5, 1)}, ${heat(1, 1)})`, display: 'inline-block' }} />
          <span>{agg.max}</span>
          <span style={{ color: '#697576' }}>Background = SCADA sorter screen (reference snapshot, not live). Badges = beds with events; red dots = photo-eyes with jams. Hover any bed for its name.</span>
        </div>
      </div>

      <OEEPanel api={api} site={site} date={date} shift={shift} />

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {sel && (
          <div style={{ ...card, borderColor: '#077398', flex: '0 0 340px' }}>
            <strong style={{ fontSize: 14 }}>{sel}</strong>
            <div style={{ fontSize: 12, color: '#697576', margin: '4px 0 8px' }}>
              {agg.bed[sel] || 0} {METRICS[metric]} · SCADA: {[...(agg.srcs[sel] || [])].join(', ') || '—'}
            </div>
            <label style={{ fontSize: 12 }}>Name<br />
              <input value={draftName} maxLength={40} onChange={e => setDraftName(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveName()} placeholder="e.g. Main loop merge" style={{ width: '100%' }} aria-label={`Name for ${sel}`} />
            </label>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn btn-primary" onClick={saveName}>Save name</button>
              <button className="btn" onClick={() => setSel(null)}>Close</button>
            </div>
          </div>
        )}
        <div style={{ ...card, flex: 1, minWidth: 420 }}>
          <strong style={{ fontSize: 14 }}>Top beds — {METRICS[metric]}</strong>
          <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 8 }}>
            <thead><tr>{['Bed', 'Name', METRICS[metric]].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {top.length === 0 && <tr><td style={{ ...td, color: '#697576' }} colSpan={3}>No events in this range.</td></tr>}
              {top.map(([id, v]) => (
                <tr key={id} onClick={() => pick(id)} style={{ cursor: 'pointer' }}>
                  <td style={td}>{id}</td><td style={td}>{names[id] || <span style={{ color: '#BBC0C1' }}>unnamed</span>}</td><td style={td}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {Object.keys(agg.unplaced).length > 0 && (
            <div style={{ fontSize: 12, color: '#697576', marginTop: 8 }}>
              Not on the map: {Object.entries(agg.unplaced).map(([s, v]) => `${s} (${v})`).join(', ')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
